import { z } from 'zod';

import { EVENT_PHASE, Icons } from '@/lib/data/domains/event';

const EventDailyScheduleActivitySchema = z.object({
  id: z.string(),
  name: z.string(),
  icon: z.nativeEnum(Icons).nullable(),
  details: z.string(),
  start: z.string().nullable(),
  end: z.string().nullable(),
  location: z.string(),
  address: z.object({
    placeId: z.string(),
    address: z.string(),
  }),
  sponsor: z.string(),
});

const EventDailyScheduleTaskSchema = z.object({
  id: z.string(),
  start: z.string().nullable(),
  end: z.string().nullable(),
  title: z.string(),
  location: z.string(),
  address: z.object({
    placeId: z.string(),
    address: z.string(),
  }),
  fmiStaffResponsibility: z.string(),
  sponsor: z.string(),
  staff: z.array(z.object({ id: z.string(), name: z.string() })),
});

const EventDailyScheduleSchema = z.object({
  id: z.string(),
  date: z.number().nullable(),
  activities: z.array(EventDailyScheduleActivitySchema).optional(),
  tasks: z.array(EventDailyScheduleTaskSchema).optional(),
});

const EventStaffRunSheetSchema = z.object({
  id: z.string(),
  title: z.string(),
  shortDescription: z.string(),
  coverImage: z.unknown(),
  ctaButton: z.string(),
  staffSchedule: z.array(EventDailyScheduleSchema),
});

const EventRegistrationSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email('Please enter valid email'),
  fabricator: z.object({ id: z.string(), name: z.string() }).nullable(),
  numberOfAttendees: z.number().nullable(),
  numberOfRooms: z.number().nullable(),
});

export const EventRegistrationFormSchema = EventRegistrationSchema.superRefine(
  (data, ctx: any) => {
    if (!data.firstName || !data.firstName.trim()) {
      ctx.addIssue({
        path: ['firstName'],
        message: 'First name is required',
        code: 'custom',
      });
    }

    if (!data.lastName || !data.lastName.trim()) {
      ctx.addIssue({
        path: ['lastName'],
        message: 'Last name is required',
        code: 'custom',
      });
    }

    if (!data.email || !data.email.trim()) {
      ctx.addIssue({
        path: ['email'],
        message: 'Email is required',
        code: 'custom',
      });
    }

    if (!data.fabricator?.id) {
      ctx.addIssue({
        path: ['fabricator'],
        message: 'Fabricator is required',
        code: 'custom',
      });
    }

    if (!data.numberOfAttendees) {
      ctx.addIssue({
        path: ['numberOfAttendees'],
        message: 'Number of attendees is required',
        code: 'custom',
      });
    }

    if (data.numberOfRooms === null || data.numberOfRooms === undefined) {
      ctx.addIssue({
        path: ['numberOfRooms'],
        message: 'Number of rooms is required',
        code: 'custom',
      });
    }
  },
);

export const EventFormSchema = z
  .object({
    id: z.string(),
    eventName: z.string(),
    sortOrder: z.number().nullable(),
    startDate: z.number().nullable(),
    endDate: z.number().nullable(),
    location: z.string(),
    address: z.object({
      placeId: z.string(),
      address: z.string(),
    }),
    coverImage: z.unknown(),
    shortDescription: z.string(),
    ctaButton: z.string(),
    showOnAppHomepage: z.boolean(),
    compactView: z.boolean(),
    videoLink: z.string(),
    phase: z.nativeEnum(EVENT_PHASE),
    agenda: z.array(EventDailyScheduleSchema),
    staffRunSheet: EventStaffRunSheetSchema,
    attendeeIds: z.array(z.string()),
    registrantIds: z.array(z.string()),
    preRegistrations: z.array(z.string()),
    updatedRegistrations: z.array(EventRegistrationSchema),
    deletedRegistrations: z.array(z.string()),
  })
  .superRefine((data, ctx: any) => {
    if (!data.eventName?.trim()) {
      ctx.addIssue({
        path: ['eventName'],
        message: 'Event name is required',
        code: 'custom',
      });
    }

    const seenDates = new Set<number>();
    data.agenda.forEach((day, index) => {
      if (day.date === null || day.date === undefined) {
        ctx.addIssue({
          path: ['agenda', index, 'date'],
          message: 'Date is required',
          code: 'custom',
        });
        return;
      }

      if (seenDates.has(day.date)) {
        ctx.addIssue({
          path: ['agenda', index, 'date'],
          message: 'Each agenda day must have a unique date',
          code: 'custom',
        });
      } else {
        seenDates.add(day.date);
      }
    });

    data.agenda.forEach((day, dayIndex) => {
      (day.activities || []).forEach((activity, activityIndex) => {
        if (!activity.start?.trim()) {
          ctx.addIssue({
            path: ['agenda', dayIndex, 'activities', activityIndex, 'start'],
            message: 'Start time is required',
            code: 'custom',
          });
        }
      });
    });

    const seenStaffRunSheetDates = new Set<number>();
    data.staffRunSheet.staffSchedule.forEach((day, index) => {
      if (day.date === null || day.date === undefined) {
        ctx.addIssue({
          path: ['staffRunSheet', 'staffSchedule', index, 'date'],
          message: 'Date is required',
          code: 'custom',
        });
        return;
      }

      if (seenStaffRunSheetDates.has(day.date)) {
        ctx.addIssue({
          path: ['staffRunSheet', 'staffSchedule', index, 'date'],
          message: 'Each staff run sheet day must have a unique date',
          code: 'custom',
        });
      } else {
        seenStaffRunSheetDates.add(day.date);
      }
    });

    data.staffRunSheet.staffSchedule.forEach((day, dayIndex) => {
      (day.tasks || []).forEach((task, taskIndex) => {
        if (!task.start?.trim()) {
          ctx.addIssue({
            path: [
              'staffRunSheet',
              'staffSchedule',
              dayIndex,
              'tasks',
              taskIndex,
              'start',
            ],
            message: 'Start time is required',
            code: 'custom',
          });
        }
      });
    });
  });
